/**
 * Cliente HTTP.
 *
 * Diferença deliberada em relação ao bzapper: lá o token vive em `localStorage`
 * e vai no header `Authorization`. Aqui a sessão é um cookie **httpOnly**, que
 * este código não consegue ler nem escrever — o navegador o anexa sozinho, desde
 * que toda requisição use `credentials: 'include'`.
 *
 * A consequência prática: não existe `getToken()`. Se você procurar por um, é
 * porque está tentando reintroduzir a vulnerabilidade que o cookie httpOnly
 * fecha — um XSS nesta página não consegue roubar a sessão.
 */

import { comecou } from '@/lib/emAndamento';

const BASE = import.meta.env.VITE_API_URL ?? 'http://localhost:8000';

export type Resposta<T> =
  | { ok: true; status: number; dados: T }
  | { ok: false; status: number; erro: string };

async function processar<T>(r: Response): Promise<Resposta<T>> {
  if (r.status === 204) return { ok: true, status: 204, dados: undefined as T };

  let payload: unknown = null;
  try {
    payload = await r.json();
  } catch {
    /* resposta sem corpo */
  }

  if (!r.ok) {
    const detalhe =
      payload && typeof payload === 'object' && 'detail' in payload
        ? String((payload as { detail: unknown }).detail)
        : 'Erro inesperado.';
    return { ok: false, status: r.status, erro: detalhe };
  }
  // O status distingue 201 (criou) de 200 (reusou/dedup) — o cadastro de contatos usa isso.
  return { ok: true, status: r.status, dados: payload as T };
}

/** `silencioso`: não acende a barra de progresso. Ver o comentário em `pedir`. */
export type Opcoes = { silencioso?: boolean };

/** Atalho para as chamadas de POLLING — as telas que se recarregam sozinhas. */
export const SILENCIOSO: Opcoes = { silencioso: true };

async function pedir<T>(
  metodo: string,
  caminho: string,
  corpo?: unknown,
  opcoes?: Opcoes,
): Promise<Resposta<T>> {
  // `comecou()` acende a barra de progresso do topo; o `finally` a apaga aconteça o que acontecer
  // — inclusive quando a rede cai, que é justamente quando deixar a barra presa seria pior.
  //
  // `silencioso` existe para o POLLING: as telas de Jobs e Sanitizar se recarregam sozinhas a cada
  // 2–4 segundos, e sem isto a barra piscaria para sempre nelas. A barra anuncia o trabalho que o
  // OPERADOR pediu; uma tela que se atualiza sozinha não é trabalho dele, é manutenção da tela.
  const terminou = opcoes?.silencioso ? () => {} : comecou();
  let r: Response;
  try {
    r = await fetch(`${BASE}${caminho}`, {
      method: metodo,
      credentials: 'include', // sem isto o cookie de sessão não viaja
      headers: corpo ? { 'Content-Type': 'application/json' } : undefined,
      body: corpo ? JSON.stringify(corpo) : undefined,
    });
  } catch {
    return { ok: false, status: 0, erro: 'Não foi possível falar com o servidor.' };
  } finally {
    terminou();
  }
  return processar<T>(r);
}

/**
 * Envia um arquivo por multipart — o upload da ingestão. O `Content-Type` NÃO é setado à mão:
 * o navegador o preenche com o `boundary` correto ao receber um `FormData`. O cookie de sessão
 * viaja por `credentials: 'include'`, como em toda requisição.
 */
export async function subirArquivo<T>(
  caminho: string,
  arquivo: File,
  contexto = '',
): Promise<Resposta<T>> {
  const form = new FormData();
  form.append('arquivo', arquivo);
  if (contexto.trim()) form.append('contexto', contexto.trim());
  const terminou = comecou();
  let r: Response;
  try {
    r = await fetch(`${BASE}${caminho}`, { method: 'POST', credentials: 'include', body: form });
  } catch {
    return { ok: false, status: 0, erro: 'Não foi possível falar com o servidor.' };
  } finally {
    terminou();
  }
  return processar<T>(r);
}

export const api = {
  get: <T>(caminho: string, opcoes?: Opcoes) => pedir<T>('GET', caminho, undefined, opcoes),
  post: <T>(caminho: string, corpo?: unknown, opcoes?: Opcoes) =>
    pedir<T>('POST', caminho, corpo, opcoes),
  put: <T>(caminho: string, corpo?: unknown) => pedir<T>('PUT', caminho, corpo),
  patch: <T>(caminho: string, corpo?: unknown) => pedir<T>('PATCH', caminho, corpo),
  del: <T>(caminho: string) => pedir<T>('DELETE', caminho),
};

// ---- Ingestão em massa por planilha (Excel/CSV) ----

/** O que a análise da planilha devolve para a tela de revisão. */
export type AnaliseImportacao = {
  importacao_id: string;
  entidade: string; // 'empresa' | 'pessoa'
  colunas: string[];
  amostra: Record<string, string>[]; // primeiras linhas, para o operador conferir o conteúdo
  atribuicoes: Record<string, string>; // coluna → chave sugerida (ou 'ignorar')
  constantes: Record<string, string>; // campo de texto → valor fixo pré-preenchido (de um molde)
  referencia_id: string | null; // referência (FK: segmento/profissão) fixa pré-preenchida (molde)
  separadores: Record<string, string>; // coluna de contato → separador (vários por célula), de molde
  chaves: Record<string, string>; // chave-alvo → descrição longa (dica/hover)
  rotulos: Record<string, string>; // chave-alvo → rótulo curto (o que o select exibe, inteiro)
  chaves_constante: Record<string, string>; // campos de texto que aceitam valor fixo p/ toda linha
  opcoes_constante?: Record<string, string[]>; // constante de vocabulário → SELECT (não texto livre)
  referencia: { rotulo: string; endpoint: string }; // o combo de referência fixa (como no cadastro)
  chave_dedup: string; // "CNPJ" | "CPF" — o identificador que atualiza em vez de duplicar
  total_linhas: number;
  via: 'ia' | 'molde' | 'manual'; // como o mapa foi preenchido ('manual' = sem IA, mapeie à mão)
};

// ---- Ingestão GLOBAL: um arquivo, várias entidades por linha, com os vínculos declarados ----

/** Uma entidade presente no arquivo: o tipo e o apelido pelo qual os vínculos a chamam. */
export type BlocoEntidade = { apelido: string; tipo: string };

/** O bloco de revisão de UMA entidade — os mesmos rótulos/fixos da importação de sempre. */
export type BlocoRevisao = BlocoEntidade & {
  titulo: string; // 'Pessoa' | 'Empresa' | …
  chaves: Record<string, string>; // chave (sem prefixo) → descrição longa
  rotulos: Record<string, string>; // chave (sem prefixo) → rótulo curto
  chaves_constante: Record<string, string>;
  opcoes_constante?: Record<string, string[]>;
  referencia: { rotulo: string; endpoint: string };
  chave_dedup: string;
};

/** Um vínculo declarado entre dois blocos ("o paciente mora no residencial"). */
export type VinculoDeclarado = {
  de: string;
  para: string;
  natureza: string;
  rotulo?: string; // texto livre que vira o nome da aresta no grafo
  cargo?: string; // só faz sentido em vínculo societário/trabalhista
};

export type AnaliseGlobal = {
  importacao_id: string;
  entidade: 'global';
  colunas: string[];
  amostra: Record<string, string>[];
  atribuicoes: Record<string, string>; // coluna → 'apelido.chave' (ou 'ignorar')
  blocos: BlocoRevisao[];
  constantes_por_bloco: Record<string, Record<string, string>>;
  referencia_por_bloco: Record<string, string>;
  separadores: Record<string, string>;
  vinculos: VinculoDeclarado[];
  chaves: Record<string, string>; // 'apelido.chave' → descrição longa
  rotulos: Record<string, string>; // 'apelido.chave' → rótulo curto
  naturezas_por_par: Record<string, string[]>; // 'apelidoA|apelidoB' → naturezas válidas
  total_linhas: number;
  via: 'ia' | 'molde' | 'manual';
};

/** Sobe a planilha marcando QUAIS entidades cada linha traz; a IA mapeia coluna→entidade.chave. */
export async function analisarPlanilhaGlobal(
  arquivo: File,
  prompt: string,
  entidades: BlocoEntidade[],
): Promise<Resposta<AnaliseGlobal>> {
  return enviarArquivoCampos<AnaliseGlobal>('/cadastro/ingestao/global', arquivo, {
    prompt,
    entidades: JSON.stringify(entidades),
  });
}

/** Confirma o mapa global revisado + os vínculos declarados → enfileira a importação. */
export async function confirmarImportacaoGlobal(
  importacaoId: string,
  dados: {
    atribuicoes: Record<string, string>;
    blocos: BlocoEntidade[];
    vinculos: VinculoDeclarado[];
    constantesPorBloco: Record<string, Record<string, string>>;
    referenciaPorBloco: Record<string, string>;
    separadores: Record<string, string>;
    tagsPorBloco: Record<string, string[]>;
    reaproveitarPorNome: Record<string, boolean>;
    molde?: { nome: string };
  },
): Promise<Resposta<{ ok: boolean; estado: string }>> {
  return api.post(`/cadastro/ingestao/importacoes/${importacaoId}/confirmar`, {
    atribuicoes: dados.atribuicoes,
    blocos: dados.blocos,
    vinculos: dados.vinculos,
    constantes_por_bloco: dados.constantesPorBloco,
    referencia_por_bloco: dados.referenciaPorBloco,
    separadores: dados.separadores,
    tags_por_bloco: dados.tagsPorBloco,
    reaproveitar_por_nome: dados.reaproveitarPorNome,
    salvar_molde: !!dados.molde,
    nome_molde: dados.molde?.nome ?? null,
  });
}

/** Estado + progresso de uma importação (a tela faz polling). */
export type EstadoImportacao = {
  id: string;
  entidade_alvo: string;
  nome_arquivo: string;
  estado: 'aguardando_revisao' | 'processando' | 'concluida' | 'erro';
  mensagem: string | null;
  total_linhas: number;
  processadas: number;
  criadas: number;
  reusadas: number;
  contatos: number;
  socios: number;
  erros: number;
  erros_detalhe: string[];
};

/** Sobe a planilha + o prompt do operador; a IA (ou um molde de layout) sugere o mapa coluna→chave. */
export async function analisarPlanilha(
  endpoint: string,
  arquivo: File,
  prompt: string,
): Promise<Resposta<AnaliseImportacao>> {
  return enviarArquivoCampos<AnaliseImportacao>(endpoint, arquivo, { prompt });
}

/** Confirma o mapa revisado (e, opcional, salva como molde) → enfileira a importação assíncrona. */
export async function confirmarImportacao(
  importacaoId: string,
  atribuicoes: Record<string, string>,
  constantes: Record<string, string>,
  referenciaId: string,
  separadores: Record<string, string>,
  tagIds: string[],
  molde?: { nome: string },
): Promise<Resposta<{ ok: boolean; estado: string }>> {
  return api.post(`/cadastro/ingestao/importacoes/${importacaoId}/confirmar`, {
    atribuicoes,
    constantes,
    referencia_id: referenciaId || null,
    separadores,
    tag_ids: tagIds,
    salvar_molde: !!molde,
    nome_molde: molde?.nome ?? null,
  });
}

/** Estado atual de uma importação — chamado em loop pela tela de progresso. */
export async function statusImportacao(
  importacaoId: string,
): Promise<Resposta<EstadoImportacao>> {
  return api.get<EstadoImportacao>(`/cadastro/ingestao/importacoes/${importacaoId}`, SILENCIOSO);
}

// ---- Gestão de Jobs (tela Processos em background) ----

/** Uma importação na lista da tela de Jobs — estado + progresso + quando. */
export type ImportacaoJob = {
  id: string;
  entidade_alvo: string;
  nome_arquivo: string;
  estado: 'aguardando_revisao' | 'processando' | 'concluida' | 'erro';
  mensagem: string | null;
  total_linhas: number;
  processadas: number;
  criadas: number;
  reusadas: number;
  contatos: number;
  erros: number;
  criado_em: string | null;
  atualizado_em: string | null;
};

export type FilaJob = {
  job_id: string | null;
  funcao: string;
  kwargs: Record<string, string>;
  enfileirado_em?: string | null;
  iniciado_em?: string | null;
  tentativa: number | null;
};

export type ResultadoJob = {
  job_id: string | null;
  funcao: string;
  sucesso: boolean;
  resultado: string;
  iniciado_em: string | null;
  terminado_em: string | null;
};

export type CronJob = {
  funcao: string;
  rotulo: string;
  quando: string;
  ultima: ResultadoJob | null;
};

export type EstadoFila = {
  worker_online: boolean;
  worker_saude: string | null;
  na_fila: FilaJob[];
  em_execucao: FilaJob[];
  recentes: ResultadoJob[];
  crons: CronJob[];
};

/** Lista todas as importações (mais recentes primeiro) para a tela de Jobs. */
export function listarImportacoesJobs(
  limite = 50,
  offset = 0,
): Promise<Resposta<{ itens: ImportacaoJob[]; total: number }>> {
  return api.get(`/cadastro/jobs/importacoes?limite=${limite}&offset=${offset}`, SILENCIOSO);
}

/** Re-enfileira uma importação travada ou com erro. */
export function reprocessarImportacao(id: string): Promise<Resposta<{ ok: boolean; estado: string }>> {
  return api.post(`/cadastro/jobs/importacoes/${id}/reprocessar`);
}

/** Retrato da fila do arq: worker vivo?, o que está na fila, em execução, recentes e crons. */
export function estadoDaFila(): Promise<Resposta<EstadoFila>> {
  return api.get<EstadoFila>('/cadastro/jobs/fila', SILENCIOSO);
}

/** Aborta um job em execução (o worker precisa aceitar abort). */
export function cancelarJob(jobId: string): Promise<Resposta<{ ok: boolean }>> {
  return api.post(`/cadastro/jobs/${jobId}/cancelar`);
}

/** Tipos de recurso compartilhado que viram nó-ponte no grafo (a base da correlação). */
export type TipoRecurso = 'telefone' | 'email' | 'endereco' | 'rede';

/**
 * Correlações (grafo) de um conjunto de recursos do mesmo tipo — contagem + donos, em lote.
 * Uma chamada por lista: a travessia da vizinhança dos nós-ponte é barata. Falha → `{}` (a UI
 * simplesmente não mostra o selo, nunca quebra).
 */
export async function buscarCorrelacoes(
  tipo: TipoRecurso,
  ids: string[],
): Promise<Record<string, CorrelacaoCad>> {
  if (ids.length === 0) return {};
  const r = await api.post<Record<string, CorrelacaoCad>>('/cadastro/grafo/correlacoes', {
    tipo,
    ids,
  });
  return r.ok ? r.dados : {};
}

/** Resolve um CEP em endereço (ViaCEP), sem persistir. `null` se o CEP não tem 8 dígitos ou falha. */
export async function consultarCep(cep: string): Promise<CepCad | null> {
  const digitos = cep.replace(/\D/g, '');
  if (digitos.length !== 8) return null;
  const r = await api.get<CepCad>(`/cadastro/cep/${digitos}`);
  return r.ok ? r.dados : null;
}

/**
 * Sobe um arquivo por multipart junto de campos de formulário nomeados — o upload de
 * documento anexo, que espera `categoria` e `nome` além do `arquivo`. Como em
 * `subirArquivo`, o `Content-Type` fica a cargo do navegador (boundary correto).
 */
export async function enviarArquivoCampos<T>(
  caminho: string,
  arquivo: File,
  campos: Record<string, string>,
): Promise<Resposta<T>> {
  const form = new FormData();
  form.append('arquivo', arquivo);
  for (const [k, v] of Object.entries(campos)) if (v.trim()) form.append(k, v.trim());
  const terminou = comecou();
  let r: Response;
  try {
    r = await fetch(`${BASE}${caminho}`, { method: 'POST', credentials: 'include', body: form });
  } catch {
    return { ok: false, status: 0, erro: 'Não foi possível falar com o servidor.' };
  } finally {
    terminou();
  }
  return processar<T>(r);
}

/**
 * Baixa um arquivo de uma rota que responde bytes (não JSON) — o CSV da exportação ou
 * um documento anexo. Faz o fetch com o cookie de sessão, transforma em blob, e dispara
 * o download no navegador. `metodo` porque a exportação é POST e o download é GET.
 */
export async function baixarArquivo(
  caminho: string,
  nomeSugerido: string,
  metodo: 'GET' | 'POST' = 'POST',
  corpo?: unknown,
): Promise<boolean> {
  // O CSV de um recorte grande é a espera mais longa do produto (o servidor transmite até 100 mil
  // linhas). O `finally` só encerra depois do `blob()`, senão a barra apagaria no primeiro byte e
  // o operador ficaria olhando uma tela parada durante o resto do download.
  const terminou = comecou();
  let blob: Blob;
  try {
    const r = await fetch(`${BASE}${caminho}`, {
      method: metodo,
      credentials: 'include',
      headers: corpo ? { 'Content-Type': 'application/json' } : undefined,
      body: corpo ? JSON.stringify(corpo) : undefined,
    });
    if (!r.ok) return false;
    blob = await r.blob();
  } catch {
    return false;
  } finally {
    terminou();
  }
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = nomeSugerido;
  document.body.append(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
  return true;
}

export type PreferenciasUsuario = {
  /** 'light' | 'dark' | 'system' — a escolha de tema, persistida na conta. */
  tema?: 'light' | 'dark' | 'system';
  /** Barra lateral colapsada (só ícones). */
  sidebar_colapsada?: boolean;
};

export type Usuario = {
  id: string;
  nome: string;
  email: string;
  telefone: string;
  papel: string;
  /** `ativo` → `inativo` → `arquivado`. Só `ativo` entra no sistema. */
  estado: 'ativo' | 'inativo' | 'arquivado';
  /** Verdadeiro enquanto a senha for a temporária enviada por e-mail. */
  deve_trocar_senha: boolean;
  /** Preferências de interface, persistidas na conta. */
  preferencias: PreferenciasUsuario;
  /** Avatar como data URL, ou null. */
  avatar: string | null;
};

export type Desafio = {
  desafio_id: string;
  canais: string[];
  expira_em: string;
};

/** Cadastro relacional — uma pessoa, shape completo do `PessoaOut` (nada oculto). */
export type PessoaCad = {
  id: string;
  nome: string;
  nome_social: string | null;
  cpf: string | null;
  rg: string | null;
  rg_orgao_emissor: string | null;
  rg_uf: string | null;
  rg_expedicao: string | null;
  cnh: string | null;
  cnh_categoria: string | null;
  cnh_validade: string | null;
  titulo_eleitor: string | null;
  pis_nis: string | null;
  passaporte: string | null;
  data_nascimento: string | null;
  idade: number | null; // derivada de data_nascimento (trigger + cron diário); só leitura
  sexo: string | null;
  estado_civil: string | null;
  nacionalidade: string | null;
  naturalidade_cidade: string | null;
  naturalidade_uf: string | null;
  nome_pai: string | null;
  nome_mae: string | null;
  escolaridade: string | null;
  profissao_id: string | null;
  renda_estimada: number | null;
  ativo: boolean;
  observacoes: string | null;
  created_at: string;
  updated_at: string;
  //: Só na criação (drawer): contatos principais opcionais. O backend os separa e vincula
  //: como qualquer contato (dedup + correlação); não voltam no PessoaOut.
  email?: string;
  telefone?: string;
};

/** Uma página do cadastro: itens + total (para "N de TOTAL"). */
export type PaginaCad<T> = {
  itens: T[];
  total: number;
};

/** Empresa (pessoa jurídica) — shape completo do `EmpresaOut`. */
export type EmpresaCad = {
  id: string;
  razao_social: string;
  nome_fantasia: string | null;
  segmento_id: string | null;
  cnpj: string | null;
  inscricao_estadual: string | null;
  inscricao_municipal: string | null;
  natureza_juridica: string | null;
  cnae_principal: string | null;
  porte: string | null;
  regime_tributario: string | null;
  capital_social: string | null;
  data_abertura: string | null;
  situacao_cadastral: string | null;
  ativo: boolean;
  observacoes: string | null;
  created_at: string;
  updated_at: string;
};

/** Condomínio — shape completo do `CondominioOut`. */
export type CondominioCad = {
  id: string;
  nome: string;
  cnpj: string | null;
  tipo: string | null;
  administradora_id: string | null;
  total_unidades: number | null;
  data_instituicao: string | null;
  ativo: boolean;
  observacoes: string | null;
  created_at: string;
  updated_at: string;
};

/** Rastreador GPS — shape do `RastreadorOut`. */
export type RastreadorCad = {
  id: string;
  unique_id: string;
  modelo: string | null;
  senha_config: string | null;
  chip_id: string | null;
  traccar_device_id: number | null;
  ativo: boolean;
  criado_em: string;
  /** Derivados pela API para a lista responder sem abrir cada ficha. */
  situacao: 'reportando' | 'silencioso' | 'mudo';
  silencio_seg: number | null;
  veiculo_placa: string | null;
  veiculo_id: string | null;
};

/** Chip (SIM) do rastreador. O `numero` é para onde vão os SMS de configuração. */
export type ChipCad = {
  id: string;
  numero: string;
  iccid: string | null;
  operadora: string | null;
  apn: string | null;
  apn_usuario: string | null;
  apn_senha: string | null;
  ativo: boolean;
  criado_em: string;
};

/** Veículo — shape completo do `VeiculoOut` (FIPE é derivado, read-only). */
export type VeiculoCad = {
  /** Derivado: há rastreador instalado e com rastreamento ligado. Não é coluna do veículo. */
  rastreado?: boolean;
  id: string;
  placa: string | null;
  renavam: string | null;
  chassi: string | null;
  tipo: string | null;
  marca: string | null;
  modelo: string | null;
  ano_fabricacao: number | null;
  ano_modelo: number | null;
  cor: string | null;
  combustivel: string | null;
  municipio: string | null;
  uf_placa: string | null;
  situacao: string | null;
  restricao: string | null;
  valor_fipe: string | null;
  fipe_codigo: string | null;
  fipe_mes_referencia: string | null;
  ativo: boolean;
  created_at: string;
  updated_at: string | null;
};

/** Processo judicial — shape completo do `ProcessoOut`. */
export type ProcessoCad = {
  id: string;
  numero_cnj: string | null;
  numero_antigo: string | null;
  segmento: string | null;
  tribunal: string | null;
  grau: string | null;
  orgao_julgador: string | null;
  comarca: string | null;
  uf: string | null;
  area: string | null;
  classe_codigo: string | null;
  classe_nome: string | null;
  valor_causa: string | null;
  data_distribuicao: string | null;
  data_autuacao: string | null;
  data_arquivamento: string | null;
  situacao: string | null;
  fase: string | null;
  sistema: string | null;
  juiz_relator: string | null;
  segredo_justica: boolean;
  justica_gratuita: boolean | null;
  prioridade: string | null;
  fonte: string | null;
  url: string | null;
  ativo: boolean;
  observacoes: string | null;
  created_at: string;
  updated_at: string;
};

/** Imóvel — shape completo do `ImovelOut`. */
export type ImovelCad = {
  id: string;
  condominio_id: string | null;
  endereco_id: string | null;
  tipo: string | null;
  finalidade: string | null;
  identificacao: string | null;
  matricula: string | null;
  cartorio: string | null;
  inscricao_iptu: string | null;
  fracao_ideal: string | null;
  area_m2: string | null;
  quartos: number | null;
  vagas: number | null;
  alugado: boolean;
  valor_venal: string | null;
  valor_mercado: string | null;
  valor_aluguel: string | null;
  observacoes: string | null;
  ativo: boolean;
  created_at: string;
};

/** Uma carteira de recebíveis. Quase tudo que ela É vem da empresa que a carrega — daí os campos
 *  derivados (`empresa_*`) virem na mesma resposta: sem eles a lista mostraria só um apelido. */
export type CarteiraCad = {
  id: string;
  empresa_id: string;
  originador_empresa_id: string | null;
  nome: string | null;
  observacoes: string | null;
  ativo: boolean;
  created_at: string;
  updated_at: string;
  empresa_razao_social: string | null;
  empresa_cnpj: string | null;
  posicoes: number;
  /** A data da fotografia mais recente — é por ela que o operador reconhece a carteira. */
  ultima_referencia: string | null;
};

export type TransacaoCad = {
  id: string;
  natureza: string;
  descricao: string | null;
  valor: string | null;
  moeda: string;
  data: string | null;
  situacao: string | null;
  fonte: string | null;
  observacoes: string | null;
  ativo: boolean;
  created_at: string;
  updated_at: string;
};

/** Um telefone com sua classificação no vínculo. */
export type ContatoTelefoneCad = {
  telefone: {
    id: string;
    raw: string;
    telefone: string | null;
    is_whatsapp: boolean;
    tipo?: string | null;
    ramal?: string | null;
  };
  classificacao: string;
  principal: boolean;
};

/** Um e-mail com sua classificação no vínculo. */
export type ContatoEmailCad = {
  email: { id: string; endereco: string };
  classificacao: string;
  principal: boolean;
};

/** Um endereço com sua classificação no vínculo (e a vigência da residência/uso). */
export type ContatoEnderecoCad = {
  endereco: {
    id: string;
    logradouro: string;
    numero: string | null;
    complemento: string | null;
    bairro: string | null;
    cidade: string;
    uf: string;
    cep: string | null;
    pais: string;
    ibge: string | null;
    latitude: string | null;
    longitude: string | null;
  };
  classificacao: string;
  principal: boolean;
  data_inicio: string | null;
  data_fim: string | null;
};

/** Endereço resolvido de um CEP (ViaCEP) — alimenta o autocomplete do formulário. */
export type CepCad = {
  encontrado: boolean;
  logradouro: string | null;
  bairro: string | null;
  cidade: string | null;
  uf: string | null;
  ibge: string | null;
  latitude: string | null;
  longitude: string | null;
};

/** A correlação de um recurso no grafo: quantos donos o compartilham e quem são. */
export type CorrelacaoCad = {
  total: number;
  donos: { tipo: string; id: string; nome: string }[];
};

/** Uma rede social com sua classificação no vínculo. */
export type ContatoRedeCad = {
  rede_social: { id: string; plataforma: string; identificador: string; url: string | null };
  classificacao: string;
  principal: boolean;
};

/** Uma relação pessoa↔pessoa (parentesco/social), temporal, com a outra ponta embutida. */
export type RelacaoPessoalCad = {
  pessoa_id: string;
  relacionada_id: string;
  tipo: string;
  data_inicio: string;
  data_fim: string | null;
  observacoes: string | null;
  created_at: string;
  relacionada: { id: string; nome: string; cpf: string | null };
};

/**
 * O que liga uma pessoa a uma empresa/instituição. `natureza` diz o quê (societário, paciente,
 * cliente…); `cargo` só existe no societário/trabalhista; `rotulo` é o nome que o operador deu ao
 * vínculo, e é o que aparece na aresta do grafo.
 */
export type EmpregoCad = {
  id: string;
  empresa: { id: string; razao_social: string; cnpj: string | null };
  natureza: string;
  cargo: { id: string; nome: string } | null;
  rotulo: string | null;
  data_inicio: string;
  data_fim: string | null;
  observacoes: string | null;
};

/** As naturezas de vínculo pessoa↔empresa, na ordem em que fazem sentido no seletor. */
export const NATUREZAS_VINCULO = [
  { valor: 'societario', rotulo: 'Societário' },
  { valor: 'trabalhista', rotulo: 'Trabalhista' },
  { valor: 'cliente', rotulo: 'Cliente' },
  { valor: 'paciente', rotulo: 'Paciente' },
  { valor: 'aluno', rotulo: 'Aluno' },
  { valor: 'morador', rotulo: 'Morador' },
  { valor: 'fornecedor', rotulo: 'Fornecedor' },
  { valor: 'outro', rotulo: 'Outro' },
] as const;

/** Cargo só faz sentido quando o vínculo é societário ou trabalhista. */
export const NATUREZA_USA_CARGO = ['societario', 'trabalhista'];

/** Uma tag: rótulo reutilizável para agrupar cadastros. `cor` é hex opcional (chip). */
export type TagCad = {
  id: string;
  nome: string;
  cor: string | null;
};

/** Um papel de uma pessoa num condomínio (síndico, morador…), com a pessoa embutida. */
export type PapelCondominioCad = {
  condominio_id: string;
  pessoa_id: string;
  pessoa: { id: string; nome: string; cpf: string | null };
  papel: string;
  data_inicio: string;
  data_fim: string | null;
  observacoes: string | null;
};

/** Um documento anexo a qualquer dono (pessoa/empresa/condomínio/imóvel/veículo/processo). */
export type DocumentoCad = {
  id: string;
  nome: string;
  categoria: string | null;
  mime: string | null;
  tamanho: number | null;
  created_at: string;
};

/** Tipo de documento cuja extração é suportada (catálogo do backend). */
export type TipoDocumentoCad = { id: string; rotulo: string; campos: string[]; owner: string };

/** Resultado da extração de um documento (campos lidos, SEM gravar nada). */
export type ExtracaoCad = {
  tipo: string;
  provedor: string;
  modelo: string;
  campos: Record<string, string>;
};

/** Catálogo de tipos de documento, opcionalmente filtrado pelo owner (pessoa/empresa) — a uma
 * empresa só se ofertam docs de PJ (Cartão CNPJ, contrato social…). */
export const listarTiposDocumento = (ownerTipo?: string) =>
  api.get<TipoDocumentoCad[]>(
    ownerTipo ? `/cadastro/documentos/tipos?owner_tipo=${ownerTipo}` : '/cadastro/documentos/tipos',
  );

export const extrairDocumento = (docId: string) =>
  api.post<ExtracaoCad>(`/cadastro/documentos/${docId}/extrair`);

/** Aplica na entidade (pessoa/empresa) os campos revisados. Devolve o gravado e o ignorado. */
export const aplicarExtracao = (
  ownerTipo: string,
  ownerId: string,
  campos: Record<string, string>,
) =>
  api.post<{ aplicados: string[]; ignorados: string[] }>(
    `/cadastro/${ownerTipo}/${ownerId}/aplicar-extracao`,
    { campos },
  );

/** Rótulos humanos dos campos da Pessoa (espelha as dicas do extrator no backend). */
export const ROTULO_CAMPO: Record<string, string> = {
  nome: 'Nome',
  cpf: 'CPF',
  rg: 'RG',
  rg_orgao_emissor: 'Órgão emissor (RG)',
  rg_uf: 'UF (RG)',
  rg_expedicao: 'Expedição (RG)',
  cnh: 'CNH',
  cnh_categoria: 'Categoria (CNH)',
  cnh_validade: 'Validade (CNH)',
  titulo_eleitor: 'Título de eleitor',
  pis_nis: 'PIS/NIS',
  passaporte: 'Passaporte',
  data_nascimento: 'Nascimento',
  sexo: 'Sexo',
  estado_civil: 'Estado civil',
  nacionalidade: 'Nacionalidade',
  naturalidade_cidade: 'Naturalidade (cidade)',
  naturalidade_uf: 'Naturalidade (UF)',
  nome_pai: 'Nome do pai',
  nome_mae: 'Nome da mãe',
  escolaridade: 'Escolaridade',
  // Pessoa jurídica (Cartão CNPJ, contrato social, inscrições).
  razao_social: 'Razão social',
  nome_fantasia: 'Nome fantasia',
  cnpj: 'CNPJ',
  inscricao_estadual: 'Inscrição estadual',
  inscricao_municipal: 'Inscrição municipal',
  natureza_juridica: 'Natureza jurídica',
  cnae_principal: 'CNAE principal',
  porte: 'Porte',
  regime_tributario: 'Regime tributário',
  capital_social: 'Capital social',
  data_abertura: 'Data de abertura',
  situacao_cadastral: 'Situação cadastral',
  // Veículo (CRLV).
  placa: 'Placa',
  renavam: 'RENAVAM',
  chassi: 'Chassi',
  marca: 'Marca',
  modelo: 'Modelo',
  ano_fabricacao: 'Ano de fabricação',
  ano_modelo: 'Ano do modelo',
  cor: 'Cor',
  combustivel: 'Combustível',
  tipo_veiculo: 'Espécie/tipo',
  municipio: 'Município (emplacamento)',
  uf_placa: 'UF da placa',
  proprietario_nome: 'Proprietário',
  proprietario_documento: 'Documento do proprietário',
  // Veículo (DUT/ATPV-e — transferência + contatos das partes).
  vendedor_nome: 'Vendedor',
  vendedor_documento: 'Documento do vendedor',
  vendedor_email: 'E-mail do vendedor',
  vendedor_cep: 'CEP do vendedor',
  vendedor_logradouro: 'Logradouro do vendedor',
  vendedor_numero: 'Número (vendedor)',
  vendedor_complemento: 'Complemento (vendedor)',
  vendedor_bairro: 'Bairro do vendedor',
  vendedor_cidade: 'Cidade do vendedor',
  vendedor_uf: 'UF do vendedor',
  comprador_nome: 'Comprador',
  comprador_documento: 'Documento do comprador',
  comprador_email: 'E-mail do comprador',
  comprador_cep: 'CEP do comprador',
  comprador_logradouro: 'Logradouro do comprador',
  comprador_numero: 'Número (comprador)',
  comprador_complemento: 'Complemento (comprador)',
  comprador_bairro: 'Bairro do comprador',
  comprador_cidade: 'Cidade do comprador',
  comprador_uf: 'UF do comprador',
  data_transferencia: 'Data da transferência',
};

/** Grafo de correlação multinível (BFS no backend): nós de entidade + recurso-ponte, arestas
 * rotuladas. `ancora` é o id do nó central; `truncado` indica que o teto de segurança foi atingido. */
export type GrafoCad = {
  ancora: string;
  /** `hub`: nó de grau alto que NÃO foi percorrido (só abre se o operador pedir); `grau` é o real. */
  nos: {
    id: string;
    tipo: string;
    rotulo: string;
    grau: number;
    hub: boolean;
    /** A que núcleo econômico o nó pertence, quando a camada está ligada. `null` = a nenhum. */
    nucleo_id: string | null;
  }[];
  arestas: { origem: string; destino: string; rotulo: string | null }[];
  truncado: boolean;
  /** id do nó → quantos vizinhos ficaram de fora dele. */
  omitidos: Record<string, number>;
};

/** Os controles da travessia do grafo — o que a tela manda na query. */
export type OpcoesGrafo = {
  saltos: number;
  correlacoes: boolean;
  ativos: boolean;
  processos: boolean;
  /** Colore por núcleo econômico em vez de por tipo. NÃO acrescenta nó nem aresta. */
  nucleo: boolean;
  expandir: string[];
};

/** O dossiê de uma pessoa: correlação por SQL. */
export type DossieCad = {
  pessoa_id: string; // id da âncora (nome herdado; vale para qualquer entidade_tipo)
  nome: string;
  cpf: string | null;
  entidade_tipo: string; // 'pessoa' | 'empresa' | 'condominio' | 'imovel' | 'veiculo' | 'processo'
  telefones: string[];
  emails: string[];
  enderecos: string[];
  vizinhos: { entidade_id: string; nome: string; tipo: string; via: string; valor: string }[];
  relacoes: { pessoa_id: string; nome: string; cpf: string | null; tipo: string }[];
  empresas: {
    empresa_id: string;
    razao_social: string;
    /** O QUE liga a pessoa à instituição, já legível: «Morador», «Sócio», ou o rótulo do operador. */
    vinculo: string;
    natureza: string | null;
    cargo: string | null;
    desde: string;
    ate: string | null;
  }[];
  condominios: { condominio_id: string; nome: string; papel: string; atual: boolean }[];
  veiculos: {
    id: string;
    placa: string | null;
    marca: string | null;
    modelo: string | null;
    fipe: number | null;
  }[];
  processos: { id: string; numero_cnj: string | null; polo: string; situacao: string | null }[];
  // Transações financeiras em que a entidade é parte, com a outra ponta (co-partes).
  transacoes: {
    id: string;
    natureza: string;
    descricao: string | null;
    papel: string;
    valor: number | null;
    data: string | null;
    co_partes: { tipo: string; id: string; rotulo: string; papel: string }[];
  }[];
  /** Quantas transações EXISTEM. `transacoes` traz no máximo o teto do dossiê — a tela diz
   *  "50 de 312" em vez de só "50", porque truncar em silêncio é mostrar um recorte como conjunto. */
  transacoes_total: number;
  /** Recebíveis em que a entidade é cedente ou sacado — com a OUTRA ponta já nomeada. */
  titulos: {
    id: string;
    seu_numero: string | null;
    papel: string;
    valor_nominal: number | null;
    venc_original: string | null;
    outra_parte: { tipo: string; id: string; rotulo: string };
    carteira: { id: string; rotulo: string };
  }[];
  titulos_total: number;
  // Vínculos estruturais quando a âncora NÃO é pessoa (sócios, moradores, donos, partes).
  partes: { tipo: string; id: string; rotulo: string; papel: string | null }[];
};

// ---------------------------------------------------------------------------
// Saneamento da base
// ---------------------------------------------------------------------------

/** Uma execução de tarefa de saneamento. `alteradas` é 0 sempre que o modo é 'relatorio'. */
export type ExecucaoSaneamento = {
  id: string;
  tarefa: string;
  modo: 'relatorio' | 'aplicar';
  estado: 'na_fila' | 'executando' | 'concluida' | 'erro';
  mensagem: string | null;
  itens: Record<string, number>;
  amostra: string[];
  alteradas: number;
  quando: string | null;
};

/** Tarefa do catálogo + a última vez que rodou. `destrutiva` = aplicar APAGA linhas. */
export type TarefaSaneamento = {
  chave: string;
  titulo: string;
  descricao: string;
  efeito: string;
  destrutiva: boolean;
  ultima: ExecucaoSaneamento | null;
};

/** Catálogo de tarefas com a última execução de cada uma — a tela inteira numa chamada. */
export function listarTarefasSaneamento(): Promise<Resposta<TarefaSaneamento[]>> {
  return api.get<TarefaSaneamento[]>('/saneamento/tarefas', SILENCIOSO);
}

/** Dispara uma tarefa. 'relatorio' só conta; 'aplicar' escreve. Devolve a execução a acompanhar. */
export function executarSaneamento(
  chave: string,
  modo: 'relatorio' | 'aplicar',
): Promise<Resposta<ExecucaoSaneamento>> {
  return api.post<ExecucaoSaneamento>(`/saneamento/tarefas/${chave}/executar`, { modo });
}

/** Estado de uma execução — a tela faz polling até sair de 'na_fila'/'executando'. */
export function estadoSaneamento(id: string): Promise<Resposta<ExecucaoSaneamento>> {
  return api.get<ExecucaoSaneamento>(`/saneamento/execucoes/${id}`, SILENCIOSO);
}

// ---------------------------------------------------------------------------
// Busca da home (linguagem natural)
// ---------------------------------------------------------------------------

/** Uma condição do recorte, já resolvida contra o vocabulário da base. */
export type CondicaoBusca = {
  alcance: 'coluna' | 'tag' | 'endereco' | 'referencia';
  campo: string;
  op: string;
  valor: unknown;
  rotulo?: string;
};

/**
 * O que o servidor entendeu. Vai e volta: paginar, refinar e exportar reenviam o PLANO, nunca a
 * frase — assim a página 2 é da mesma busca, e o CSV traz o mesmo recorte que a tela mostrou.
 */
export type PlanoBusca = {
  modo: 'identificador' | 'texto' | 'recorte';
  entidade: string | null;
  texto: string | null;
  condicoes: CondicaoBusca[];
  interpretacao: string[];
  /** Viaja no plano para o aviso âmbar sobreviver a tirar um chip e reexecutar. */
  nao_entendido?: string[];
  /** `deterministico` | `misto` — a IA só entra no que o resolvedor não leu. */
  origem: string;
};

export type ItemBusca = { tipo: string; id: string; rotulo: string; sub?: string };

export type RespostaBusca = {
  modo: 'identificador' | 'texto' | 'recorte';
  pergunta: string;
  plano?: PlanoBusca;
  // identificador
  identificador?: { tipo: string; valor: string };
  achados?: { tipo: string; id: string; rotulo: string; via: string }[];
  // texto
  grupos?: { tipo: string; total: number; itens: ItemBusca[] }[];
  // recorte
  alvo?: string;
  interpretacao?: string[];
  alternativas?: { rotulo: string; condicoes: CondicaoBusca[] }[];
  nao_entendido?: string[];
  itens?: ItemBusca[];
  limite?: number;
  offset?: number;
  /** O que ESTE recorte suporta — a tela não oferece o que o servidor recusaria. */
  acoes?: { lista: boolean; csv: boolean; tag: boolean };
  total: number;
};

/** Pergunta em português (ou reexecuta um plano). */
export function buscar(corpo: {
  pergunta?: string;
  plano?: PlanoBusca;
  limite?: number;
  offset?: number;
}): Promise<Resposta<RespostaBusca>> {
  return api.post<RespostaBusca>('/cadastro/busca', corpo);
}

/** Aplica uma tag ao recorte INTEIRO. `marcados < alcancados` = parte já estava marcada. */
export function marcarRecorte(
  plano: PlanoBusca,
  nome: string,
): Promise<Resposta<{ tag: { id: string; nome: string }; alcancados: number; marcados: number }>> {
  return api.post('/cadastro/busca/tag', { plano, nome });
}
